'use client';

import React, { useMemo } from 'react';
import * as d3 from 'd3';

export interface DataItem extends d3.SimulationNodeDatum {
  name: string;
  value: number;
  color?: string;
  diameter?: number;
  fx?: number;
  fy?: number;
}

export interface MedBubbleProps {
  data: DataItem;
  circleProps?: React.SVGProps<SVGCircleElement>;
  textProps?: React.SVGProps<SVGTextElement>;
}

const computeDiameter = (value: number, scaleFactor = 50) => {
  const computed = 2 * Math.sqrt(value * scaleFactor);
  return Math.max(60, computed);
};

/**
 * Wraps text to fit within maxWidth, returning an array of lines.
 * Truncates text over 19 characters with ellipsis.
 */
const wrapText = (
  text: string,
  fontSize: number,
  maxWidth: number,
  fontFamily = 'sans-serif'
): string[] => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return [text];
  
  context.font = `${fontSize}px ${fontFamily}`;
  
  // Truncate text if over 19 characters
  let processedText = text;
  if (text.length > 19) {
    processedText = text.substring(0, 19).trim() + '…';
  }
  
  const words = processedText.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = context.measureText(testLine).width;
    
    if (testWidth <= maxWidth || !currentLine) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.length > 0 ? lines : [processedText];
};

const MedBubble: React.FC<MedBubbleProps> = ({
  data,
  circleProps,
  textProps,
}) => {
  // Compute diameter (or use stored value), ensuring a minimum size.
  const diameter = data.diameter ?? computeDiameter(data.value, 75);

  const radius = diameter / 2;

  // Font sizes scale with the bubble's diameter.
  const valueFontSize = diameter * 0.2; // for numeric value
  const percentFontSize = valueFontSize * 0.75; // for "%" symbol
  
  // Calculate optimal font size and wrapping for the name
  const { fontSize: nameFontSize, lines: wrappedLines } = useMemo(() => {
    const availableHeight = radius * 0.6; // Height available below the percentage
    const maxNameWidth = diameter * 0.75; // Width constraint
    let fontSize = diameter * 0.15; // Start with ideal font size
    
    // Try different font sizes until text fits
    for (let attempts = 0; attempts < 10; attempts++) {
      const lines = wrapText(data.name, fontSize, maxNameWidth, 'sans-serif');
      const totalHeight = lines.length * fontSize * 1.1;
      
      if (totalHeight <= availableHeight) {
        return { fontSize, lines };
      }
      
      fontSize *= 0.9; // Reduce font size by 10%
    }
    
    // Fallback: use smallest font size with maximum 2 lines
    const lines = wrapText(data.name, fontSize, maxNameWidth, 'sans-serif').slice(0, 2);
    return { fontSize, lines };
  }, [data.name, diameter, radius]);

  // Calculate total height of both percentage and name text
  const percentageHeight = valueFontSize;
  const nameTextHeight = wrappedLines.length * nameFontSize * 1.1;
  const spacing = nameFontSize * 0.25; // gap-1 equivalent spacing between percentage and name
  const totalTextHeight = percentageHeight + spacing + nameTextHeight;
  
  // Center the entire text block vertically
  const startY = -totalTextHeight / 2;
  const percentageY = startY + percentageHeight / 2;
  const nameStartY = startY + percentageHeight + spacing;

  return (
    <g transform={`translate(${data.x ?? 0}, ${data.y ?? 0})`}>
      <circle
        cx={0}
        cy={0}
        r={radius}
        fill={data.color}
        stroke="none"
        {...circleProps}
      />
      <text
        x={0}
        y={percentageY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#fff"
        {...textProps}
      >
        <tspan fontSize={valueFontSize} fontWeight="800">
          {data.value}
        </tspan>
        <tspan
          dx="1"
          dy="-0.3em"
          fontSize={percentFontSize}
          fontWeight="normal"
        >
          %
        </tspan>
      </text>
      <text
        x={0}
        y={nameStartY}
        textAnchor="middle"
        dominantBaseline="hanging"
        fill="#fff"
        fontSize={nameFontSize}
        fontWeight="400"
        {...textProps}
      >
        {wrappedLines.map((line, index) => (
          <tspan
            key={index}
            x={0}
            dy={index === 0 ? 0 : `${nameFontSize * 1.1}px`}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export default MedBubble;
